import api from '../core/api';
export type {
  MasterCatalogGroup,
  MasterCatalogType,
  MasterSchema,
  MasterFieldDef,
} from '../../constants/masters';

export async function getMasterCatalog(opts?: { scope?: 'common' | 'service'; hub?: string; slug?: string }) {
  const res = await api.get('/admin/masters/catalog', { params: opts });
  return res.data.data as import('../../constants/masters').MasterCatalogGroup[];
}

export async function getMasterSchema(type: string) {
  const res = await api.get(`/admin/masters/schema/${type}`);
  return res.data.data as import('../../constants/masters').MasterSchema;
}

export async function getMasters(type: string, params?: {
  search?: string;
  page?: number;
  limit?: number;
  parentId?: string;
  rootsOnly?: boolean;
  activeOnly?: boolean;
  country?: string;
  state?: string;
  status?: 'active' | 'inactive';
  /** Include [Deprecated] rows (default: hidden). */
  includeDeprecated?: boolean;
  /** Return only deprecated rows (implies includeDeprecated). */
  deprecatedOnly?: boolean;
}) {
  const res = await api.get(`/admin/masters/${type}`, {
    params: {
      ...params,
      rootsOnly: params?.rootsOnly ? true : undefined,
      activeOnly: params?.activeOnly ? true : undefined,
      includeDeprecated: params?.includeDeprecated ? true : undefined,
      deprecatedOnly: params?.deprecatedOnly ? true : undefined,
    },
  });
  return res.data;
}

export async function createMaster(type: string, payload: Record<string, unknown>) {
  const res = await api.post(`/admin/masters/${type}`, payload);
  return res.data.data;
}

export async function updateMaster(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/admin/masters/${id}`, payload);
  return res.data.data;
}

export async function deleteMaster(id: string) {
  const res = await api.delete(`/admin/masters/${id}`);
  return res.data.data;
}

export interface StateSubdivision {
  isoCode: string;
  code: string;
  name: string;
  latitude?: string;
  longitude?: string;
}

export interface StateReference {
  country: string;
  countryName: string;
  subdivisions: StateSubdivision[];
}

export interface StateCountrySummary {
  code: string;
  name: string;
  total: number;
  active: number;
}

export interface StateUsage {
  cities: number;
  zones: number;
  dispatchZones: number;
  addresses: number;
  total: number;
}

/** Official ISO 3166-2 subdivisions of a country. */
export async function getStateReference(country: string) {
  const res = await api.get('/admin/masters/states/reference', { params: { country } });
  return res.data.data as StateReference;
}

/** Countries that have states, with counts — drives the list's country filter. */
export async function getStateCountries() {
  const res = await api.get('/admin/masters/states/countries');
  return res.data.data as StateCountrySummary[];
}

/** Cities, zones and addresses that still reference a state. */
export async function getStateUsage(id: string) {
  const res = await api.get(`/admin/masters/states/${id}/usage`);
  return res.data.data as StateUsage;
}

export interface CityReference {
  country: string;
  countryName: string;
  state: string;
  cities: Array<{ name: string; latitude?: string; longitude?: string }>;
}

/** Known cities of a state (country-state-city reference data). */
export async function getCityReference(country: string, state: string) {
  const res = await api.get('/admin/masters/cities/reference', { params: { country, state } });
  return res.data.data as CityReference;
}

/** Zones and addresses that still reference a city. */
export async function getCityUsage(id: string) {
  const res = await api.get(`/admin/masters/cities/${id}/usage`);
  return res.data.data as Omit<StateUsage, 'cities'> & { cities?: number };
}

export interface CurrencyReferenceItem {
  code: string;
  name: string;
  symbol: string;
  numeric: string;
  decimals: number;
  countries: string[];
  autoRateSupported: boolean;
}

export interface CurrencyReference {
  baseCurrency: string;
  baseAutoRateSupported: boolean;
  rateSources: Array<{ value: string; label: string; automatic: boolean; description: string }>;
  currencies: CurrencyReferenceItem[];
}

export interface CurrencyUsage {
  isBase: boolean;
  countries: number;
  currencyProfile: number;
  tenantDefault: boolean;
  payments: number;
  wallets: number;
  total: number;
}

/** ISO 4217 currencies, the tenant base currency and supported rate sources. */
export async function getCurrencyReference() {
  const res = await api.get('/admin/masters/currencies/reference');
  return res.data.data as CurrencyReference;
}

export interface TimezoneReferenceItem {
  ianaId: string;
  code: string;
  name: string;
  abbreviation: string;
  utcOffset: string;
  dstOffset: string | null;
  dstSupported: boolean;
  countries: string[];
}

export interface TimezoneUsage {
  countries: number;
  tenantDefault: boolean;
  systemSetting: boolean;
  zones: number;
  workingCalendars: number;
  total: number;
}

/** Supported IANA timezones with derived code, offsets, DST and abbreviation. */
export async function getTimezoneReference() {
  const res = await api.get('/admin/masters/timezones/reference');
  return res.data.data as { timezones: TimezoneReferenceItem[] };
}

/** Countries, tenant default, settings, zones and calendars that still use a timezone. */
export async function getTimezoneUsage(id: string) {
  const res = await api.get(`/admin/masters/timezones/${id}/usage`);
  return res.data.data as TimezoneUsage;
}

export type AddressFieldType = 'text' | 'textarea' | 'number' | 'pincode' | 'select';
export type AddressFieldSource = 'masters' | 'google_places' | 'manual';

export interface AddressGeoLevelDef {
  value: string;
  label: string;
  fieldKey: string | null;
  parent: string | null;
  fieldTypes: AddressFieldType[];
  sources: AddressFieldSource[];
  core: boolean;
  anchor: boolean;
  repeatable: boolean;
}

export interface AddressFieldReference {
  geoLevels: AddressGeoLevelDef[];
  fieldTypes: Array<{ value: AddressFieldType; label: string; hint: string }>;
  sources: Array<{ value: AddressFieldSource; label: string; hint: string }>;
}

/** Geo levels with their allowed field types / data sources, hierarchy parent and core flags. */
export async function getAddressFieldReference() {
  const res = await api.get('/admin/masters/address_fields/reference');
  return res.data.data as AddressFieldReference;
}

export interface PaymentGatewayStatus {
  value: string;
  label: string;
  kind: 'online' | 'offline';
  ready: boolean;
  sandbox: boolean;
  reason: string | null;
}

export interface PaymentMethodReference {
  gateways: PaymentGatewayStatus[];
  feeBases: Array<{ value: 'platform' | 'gateway' | 'both'; label: string; hint: string }>;
  maxFeePercent: number;
}

export interface BankIfscLookup {
  ifsc: string;
  ifscPrefix: string;
  bankName: string | null;
  branch: string | null;
  city: string | null;
  state: string | null;
  swiftCode: string | null;
  source: 'razorpay' | 'directory' | 'none';
  existing: { _id: string; name: string; isActive: boolean } | null;
}

export async function lookupBankIfsc(ifsc: string) {
  const res = await api.get(`/admin/masters/banks/ifsc/${encodeURIComponent(ifsc)}`);
  return res.data.data as BankIfscLookup;
}

export async function getPaymentMethodReference() {
  const res = await api.get('/admin/masters/payment_methods/reference');
  return res.data.data as PaymentMethodReference;
}

export interface LanguageReferenceItem {
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  defaultLocale: string;
  locales: string[];
  hasCatalog: boolean;
}

export interface LanguageReference {
  defaultLanguage: string;
  languages: LanguageReferenceItem[];
}

/** ISO 639-1 languages with native names, script direction and locales (BCP 47). */
export async function getLanguageReference() {
  const res = await api.get('/admin/masters/languages/reference');
  return res.data.data as LanguageReference;
}

/** Countries, settings, payments and wallets that still reference a currency. */
export async function getCurrencyUsage(id: string) {
  const res = await api.get(`/admin/masters/currencies/${id}/usage`);
  return res.data.data as CurrencyUsage;
}

export interface PromoCouponStat {
  tenantId: string;
  code: string;
  usedCount: number;
  usageLimit: number;
  redemptions: number;
  uniqueCustomers: number;
  totalDiscount: number;
  lastUsedAt: string | null;
  liveAtCheckout: boolean;
}

/** Redemption counts per coupon; the server also switches off coupons whose Valid To has passed. */
export async function getPromoCouponStats() {
  const res = await api.get('/admin/masters/app_promo_coupons/stats');
  return res.data.data as { expired: number; stats: PromoCouponStat[] };
}

export async function getMasterOptions(
  type: string,
  params?: { parentType?: string; country?: string; state?: string }
) {
  const res = await api.get(`/admin/masters/${type}/parent-options`, { params });
  return res.data.data as Array<{
    _id: string;
    code: string;
    name: string;
    country?: string;
    state?: string;
    parentId?: string | null;
    currency?: string;
    timezone?: string;
    timezones?: string[];
    currencies?: string[];
  }>;
}

export interface CountryLocaleDefaults {
  weekendDays: number[];
  firstDayOfWeek: '0' | '1' | '6';
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  numberFormat: 'en-IN' | 'en-US' | 'de-DE';
  taxSystem: 'gst' | 'vat' | 'sales_tax' | 'none';
  gstVatEnabled: boolean;
  invoicePrefix: string;
  postalCodePattern: string;
  addressFormat: string[];
  nationality: string;
  measurementSystem: 'metric' | 'imperial';
  distanceUnit: 'km' | 'mi';
  weightUnit: 'kg' | 'lb';
  temperatureUnit: 'celsius' | 'fahrenheit';
  gpsFormat: 'decimal' | 'dms';
  emergencyNumbers: Record<string, string>;
}

export interface CountryReference {
  code: string;
  iso3: string;
  name: string;
  native: string;
  flag: string;
  phoneCode: string;
  capital: string;
  continent: string;
  currencies: string[];
  languages: string[];
  timezones: string[];
  timezone: string;
  latitude?: string;
  longitude?: string;
  defaults: CountryLocaleDefaults;
}

export interface CountryReferenceBundle {
  countries: CountryReference[];
  languages: Array<{ code: string; name: string; native: string }>;
  currencies: string[];
  addressFormatTokens: string[];
  emergencyServiceKeys: string[];
}

/** ISO country reference data (ISO3, dial code, currencies, languages, timezones, locale defaults). */
export async function getCountryReference() {
  const res = await api.get('/admin/masters/countries/reference');
  return res.data.data as CountryReferenceBundle;
}

export async function bulkImportMasters(type: string, rows: Record<string, unknown>[]) {
  const res = await api.post(`/admin/masters/${type}/bulk-import`, { rows });
  return res.data.data as {
    created: number;
    updated: number;
    failed: { code: string; error: string }[];
  };
}
